import { textEncoder, textDecoder, bytesToHex, hexToBytes } from './utils.js'
import { encrypt, decrypt } from './crypto.js'
import { randomBytes } from '@noble/hashes/utils'
import { CRYPTO_CONSTANTS } from './constants.js'

/**
 * @typedef {import('./types.js').InterWorkerMessage} InterWorkerMessage
 * @typedef {import('./types.js').RequestMessageData} RequestMessageData
 * @typedef {import('./types.js').ResponseMessageData} ResponseMessageData
 */

/**
 * Packs a message for inter-worker communication with end-to-end encryption
 *
 * @param {Uint8Array} privKey - Sender's Ed25519 private key
 * @param {Object} params
 * @param {Uint8Array} params.publicKey - Sender's Ed25519 public key
 * @param {string} params.receiver_public_key - Receiver's public key (hex)
 * @param {string} params.expire_at - ISO 8601 expiration timestamp
 * @param {RequestMessageData|ResponseMessageData} params.data - Message payload
 * @returns {Promise<InterWorkerMessage>} Encrypted inter-worker message
 */
export const packInterWorkerMessage = async (
  privKey,
  { publicKey, receiver_public_key, expire_at, data },
) => {
  // Serialize data to JSON bytes
  const dataBytes = textEncoder.encode(JSON.stringify(data))

  // Parse receiver's public key
  const receiverPublicKeyBytes = hexToBytes(receiver_public_key)

  // Generate random salt for this message (prevents key reuse)
  const salt = randomBytes(CRYPTO_CONSTANTS.SALT_LENGTH)

  // Encrypt data with end-to-end encryption
  const ciphertext = await encrypt(privKey, receiverPublicKeyBytes, dataBytes, salt)

  return {
    sender_public_key: bytesToHex(publicKey),
    receiver_public_key,
    expire_at,
    data: bytesToHex(ciphertext),
    salt: bytesToHex(salt),
  }
}

/**
 * Unpacks and decrypts an inter-worker message
 *
 * @param {Uint8Array} privKey - Receiver's Ed25519 private key
 * @param {Object} params
 * @param {string} params.sender_public_key - Sender's public key (hex)
 * @param {string} params.data - Encrypted data (hex)
 * @param {string} params.salt - Salt used for encryption (hex)
 * @returns {Promise<RequestMessageData|ResponseMessageData>} Decrypted message data
 * @throws {Error} If decryption fails (wrong key or tampered data)
 */
export const unpackInterWorkerMessageData = async (
  privKey,
  { sender_public_key, data, salt },
) => {
  // Parse hex-encoded fields
  const ciphertextBytes = hexToBytes(data)
  const saltBytes = hexToBytes(salt)
  const senderPublicKeyBytes = hexToBytes(sender_public_key)

  // Decrypt data with end-to-end encryption
  const plaintextBytes = await decrypt(
    privKey,
    senderPublicKeyBytes,
    ciphertextBytes,
    saltBytes,
  )

  // Parse JSON and return message data
  return JSON.parse(textDecoder.decode(plaintextBytes))
}
